import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import {
  listContracts,
  createContract,
  updateContract,
  deleteContract,
  listEntityRows,
  reorderList,
} from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";

// The committed contract lines under one order. `amount` is the payable cost; the
// order's Contract Given is the sum of these. Inline rapid-entry like the quote grid.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

const EMPTY = {
  floor_id: "",
  room_id: "",
  quantity_type_id: "",
  description: "",
  quantity: "",
  unit_price: "",
  quoted_allowance: "",
  amount: "",
  comments: "",
};

function ContractsPanel({ claimId, orderId, onChanged }) {
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({});
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  function load() {
    return listContracts(claimId, orderId)
      .then(setItems)
      .catch(() => setError("Unable to load contract lines."));
  }
  useEffect(() => {
    load();
    Promise.all(
      ["floors", "rooms", "quantity-types"].map((k) =>
        listEntityRows(k).then((r) => [k, r]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, orderId]);

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  const total = items.reduce((a, it) => a + Number(it.amount || 0), 0);

  function startAdd() {
    setError("");
    setForm(EMPTY);
    setEditingId("new");
  }
  function startEdit(it) {
    setError("");
    setForm({
      floor_id: it.floor_id || "",
      room_id: it.room_id || "",
      quantity_type_id: it.quantity_type_id || "",
      description: it.description || "",
      quantity: it.quantity ?? "",
      unit_price: it.unit_price ?? "",
      quoted_allowance: it.quoted_allowance ?? "",
      amount: it.amount ?? "",
      comments: it.comments || "",
    });
    setEditingId(it.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") await createContract(claimId, orderId, form);
      else await updateContract(claimId, orderId, editingId, form);
      setEditingId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    }
  }
  async function remove(it) {
    if (!window.confirm("Delete this contract line?")) return;
    try {
      await deleteContract(claimId, orderId, it.id);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  // Drag-and-drop: apply optimistically, persist the full order, revert on failure.
  const dragDisabled = editingId !== null;
  async function handleReorder(reordered) {
    const prev = items;
    setItems(reordered);
    try {
      await reorderList(claimId, `orders/${orderId}/contracts`, reordered.map((l) => l.id));
    } catch (err) {
      setItems(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  function sel(field, key, placeholder = "— None —") {
    return (
      <ThemedSelect size="sm" value={form[field]} onChange={(e) => set({ [field]: e.target.value })}>
        <option value="">{placeholder}</option>
        {(lookups[key] || []).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </ThemedSelect>
    );
  }

  function editRow(key) {
    return (
      <tr key={key}>
        <td colSpan={8} className="bg-body-tertiary">
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}><Form.Label className="mb-0 small fw-semibold">Floor</Form.Label>{sel("floor_id", "floors")}</Col>
            <Col xs={6} md={2}><Form.Label className="mb-0 small fw-semibold">Room</Form.Label>{sel("room_id", "rooms")}</Col>
            <Col xs={12} md={5}>
              <Form.Label className="mb-0 small fw-semibold">Description</Form.Label>
              <Form.Control as="textarea" rows={1} size="sm" value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </Col>
            <Col xs={6} md={3}><Form.Label className="mb-0 small fw-semibold">Unit</Form.Label>{sel("quantity_type_id", "quantity-types", "—")}</Col>
            <Col xs={6} md={2}><Form.Label className="mb-0 small fw-semibold">Quantity</Form.Label><Form.Control size="sm" type="number" step="0.01" value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} /></Col>
            <Col xs={6} md={2}><Form.Label className="mb-0 small fw-semibold">Unit Price</Form.Label><Form.Control size="sm" type="number" step="0.01" value={form.unit_price} onChange={(e) => set({ unit_price: e.target.value })} /></Col>
            <Col xs={6} md={3}><Form.Label className="mb-0 small fw-semibold">Quoted Allowance</Form.Label><Form.Control size="sm" type="number" step="0.01" value={form.quoted_allowance} onChange={(e) => set({ quoted_allowance: e.target.value })} /></Col>
            <Col xs={6} md={3}><Form.Label className="mb-0 small fw-semibold">Amount (cost)</Form.Label><Form.Control size="sm" type="number" step="0.01" value={form.amount} onChange={(e) => set({ amount: e.target.value })} /></Col>
            <Col xs={12} className="d-flex gap-2">
              <Button size="sm" onClick={save}>{key === "new" ? "Add" : "Save"}</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
            </Col>
          </Row>
        </td>
      </tr>
    );
  }

  function viewRow(it, idx) {
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled}>
        <td className="text-muted">{idx + 1}</td>
        <td className="small">{[it.floor_name, it.room_name].filter(Boolean).join(" / ") || <span className="text-muted">—</span>}</td>
        <td style={{ whiteSpace: "pre-wrap", minWidth: 160 }}>{it.description || <span className="text-muted">—</span>}</td>
        <td className="text-end text-nowrap">{Number(it.quantity)}{it.quantity_type_name ? <span className="text-muted"> {it.quantity_type_name}</span> : null}</td>
        <td className="text-end text-nowrap">{money(it.unit_price)}</td>
        <td className="text-end text-nowrap">{money(it.amount)}</td>
        <td className="text-end text-nowrap">
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => startEdit(it)}>Edit</Button>
          <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>Delete</Button>
        </td>
      </SortableTr>
    );
  }

  return (
    <div className="border rounded p-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fw-semibold small">Contract lines</span>
        {editingId === null ? (
          <Button size="sm" variant="success" onClick={startAdd}>+ Add Contract Line</Button>
        ) : null}
      </div>
      {error ? <div className="alert alert-danger py-1 my-1">{error}</div> : null}
      <SortableRows items={items} onReorder={handleReorder} disabled={dragDisabled}>
      <Table bordered hover size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 36 }} />
            <th style={{ width: 30 }}>#</th>
            <th>Floor / Room</th>
            <th>Description</th>
            <th className="text-end">Qty</th>
            <th className="text-end">Unit Price</th>
            <th className="text-end">Amount</th>
            <th className="text-end" style={{ width: 130 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (editingId === it.id ? editRow(it.id) : viewRow(it, idx)))}
          {editingId === "new" ? editRow("new") : null}
          {items.length === 0 && editingId !== "new" ? (
            <tr><td colSpan={8} className="text-center text-muted">No contract lines.</td></tr>
          ) : null}
        </tbody>
        {items.length > 0 ? (
          <tfoot>
            <tr className="table-surface">
              <td colSpan={6} className="text-end fw-semibold">Contract Given</td>
              <td className="text-end text-nowrap fw-semibold">{money(total)}</td>
              <td />
            </tr>
          </tfoot>
        ) : null}
      </Table>
      </SortableRows>
    </div>
  );
}

export default ContractsPanel;
